import React, { useCallback, useContext, useMemo, useState } from "react";
import useHandleError from "Utils/handleError";
import { useHistory, generatePath } from "react-router-dom";
import { Amplitude, useAmplitude } from "react-amplitude-hooks";
import { useFormatNumber } from "Utils/formatNumber";
import api from "Api";
import Fade from "Components/shared/Fade";
import fileSaver from "Utils/fileSaver";
import Header from "Components/shared/Header";
import Table from "Components/shared/Table/Table";
import { getRedeemTableColumns } from "Helpers/redemption";
import i18nextTranslate from "Lang/i18nextTranslate";
import { i18nextKeys } from "Lang/i18nextKeys";
import { ROUTES } from "Router/Routes";
import GlobalFilter from "Components/shared/Table/GlobalFilter";
import { LangContext } from 'States/lang/langState';
import { UiContext } from "States/ui/uiState";
import { Button } from "Components/shared/buttons";

const RedeemManagementOverview = () => {
  const formatNumber = useFormatNumber();
  const PAGE_SIZE = 20;
  const handleError = useHandleError();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [pageCount, setPageCount] = React.useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const fetchIdRef = React.useRef(0);

  const { lang } = useContext(LangContext);
  const {
    breakpoints: { md, xxl },
  } = useContext(UiContext);
  const { logEvent } = useAmplitude();

  const fetchData = useCallback((pageIndex, pageSize, [orderBy], _filters) => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);

    (async () => {
      try {
        // Abort if this is not the latest fetch
        if (fetchId !== fetchIdRef.current) {
          return;
        }

        const globalFilterOdata = `(contains(ReferenceCode, '${globalFilter}') ` +
          `or contains(concat(concat(UserFirstName, ' '), UserLastName), '${globalFilter}'))`;
        
        const sort = orderBy?.desc ? 'desc' : 'asc';
        const orderByString = orderBy?.id === 'User'
          ? `UserFirstName ${sort}, UserLastName ${sort}`
          : `${orderBy?.id} ${sort}`

        const res = await api.Redemptions.list({
          orderBy: orderBy && orderByString,
          top: pageSize,
          skip: pageSize * pageIndex,
          filter: globalFilter && globalFilterOdata
        });

        const { value } = res;
        const totalCount = res["@odata.count"];

        setPageCount(Math.ceil(totalCount / PAGE_SIZE));
        setRedemptionRequests(value);
        setIsLoading(false);
      } catch (error) {
        const message = i18nextTranslate(
          i18nextKeys.errorRedeemManagementDetailsLoadingError
        );
        handleError({ error, message });
      }
    })();
  });

  const columns = useMemo(
    () => getRedeemTableColumns({ formatNumber, xxlScreenSize: xxl }),
    [lang]
  );

  const toRedemptionRequestDetails = ({ original: { Id } }) => {
    history.push(generatePath(ROUTES.admin.redemptionDetails, { id: Id }));
  };

  const exportRedemptions = async () => {
    try {
      const data = await api.Redemptions.export();
      fileSaver(data, "redemptions-export.xlsx");
    } catch (error) {
      const message = i18nextTranslate(
        i18nextKeys.errorRedeemManagementRedeemRecordsExportFailure
      );
      handleError({ error, message });
    }
  };

  const search = (filter) => {
    if (filter) {
      logEvent("Redemption search", {
        parameter: filter
      });
    }
    setGlobalFilter(filter);
  };

  const buttonWidth = {
    xxl: "190px",
    md: "170px",
    default: "100%"
  };

  return (
    <div className="select-none" data-qa="admin-redeem">
      <Fade show={true}>
        <Header text={i18nextTranslate(i18nextKeys.headerRedemptionManagement)}>
          <Amplitude>
            {({ instrument }) => (
              <div className="flex flex-col md:flex-row gap-10 md:gap-16">
                <Button
                  dataQa="admin-redeem-exportButton"
                  onClick={instrument("Export redemptions", exportRedemptions)}
                  text={i18nextTranslate(i18nextKeys.buttonExport)}
                  width={buttonWidth}
                />
                <GlobalFilter
                  globalFilter={globalFilter}
                  setGlobalFilter={search}
                  placeholder={i18nextTranslate(i18nextKeys.commonSearchCodeUser)}
                  width={!md ? "100%" : null}
                />
              </div>
            )}
          </Amplitude>
        </Header>

        <div className="w-full flex flex-col items-center">
          <Table
            dataQa="admin-redeem"
            defaultSortBy="ReceivedOn"
            defaultPageSize={PAGE_SIZE}
            pageCount={pageCount}
            columns={columns}
            data={redemptionRequests}
            isLoading={isLoading}
            outerGlobalFilter={globalFilter}
            getRowProps={(row) => ({
              onClick: () => toRedemptionRequestDetails(row)
            })}
            fetchData={fetchData}
            lang={lang}
          />
        </div>
      </Fade>
    </div>
  );
};

export default RedeemManagementOverview;
